'use client';

import { useEffect } from 'react';

/**
 * Global friction against casually saving images/videos: disables the right-click
 * context menu and drag-to-save on <img>/<video> elements (and anything marked
 * data-protected-media). This is a UX nudge toward the download button — NOT a
 * security boundary. The real protection is the watermark baked into the served
 * pixels (screenshots and devtools can't be blocked, and we don't pretend to).
 *
 * Scope is narrow: the handlers only preventDefault when the target is media, so
 * right-click / selection on text, links and buttons everywhere else is untouched.
 */
export default function MediaProtection() {
  useEffect(() => {
    const isMedia = (el: EventTarget | null): boolean => {
      const node = el as HTMLElement | null;
      if (!node || !node.tagName) return false;
      const tag = node.tagName.toUpperCase();
      if (tag === 'IMG' || tag === 'VIDEO') return true;
      return typeof node.closest === 'function' && !!node.closest('[data-protected-media]');
    };

    const onContextMenu = (e: MouseEvent) => {
      if (isMedia(e.target)) e.preventDefault();
    };
    const onDragStart = (e: DragEvent) => {
      const node = e.target as HTMLElement | null;
      if (node?.tagName?.toUpperCase() === 'IMG') e.preventDefault();
    };

    document.addEventListener('contextmenu', onContextMenu);
    document.addEventListener('dragstart', onDragStart);
    return () => {
      document.removeEventListener('contextmenu', onContextMenu);
      document.removeEventListener('dragstart', onDragStart);
    };
  }, []);

  return null;
}
